import { OWNED_SUPABASE_URL, OWNED_SUPABASE_PUBLISHABLE_KEY } from "@/lib/owned-supabase-config";
import { recalcMilestonesForUserSafe } from "@/lib/milestone-recalc.server";
import { resolveEditableNominee, isSubjectError } from "@/lib/profile-subject.server";
import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { requireSupabaseAuth } from "@/lib/auth/require-auth";
import type { Database } from "@/integrations/supabase/types";
import { cleanCityLabel } from "@/lib/label-normalize";
import { isProfilePubliclyVisible } from "@/lib/profile-visibility";
import { applyIndependentToggle, applySalonLink } from "@/lib/affiliation-state";





/* ------------------------------------------------------------------ */
/* Public-safe salon shape (never expose submitter identity, notes, etc.) */

export type SalonSummary = {
  id: string;
  slug: string;
  name: string;
  city: string | null;
  state_code: string | null;
  status: "pending" | "verified" | "inactive";
  logo_url: string | null;
  claimed?: boolean;
  suggested_by_me?: boolean;
  /** True only for the claimed owner, or the creator while still unclaimed. */
  can_edit?: boolean;

};

export type SalonDetail = SalonSummary & {
  description: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  postal_code: string | null;
  raw_city: string | null;
  raw_state: string | null;
  phone: string | null;
  website: string | null;
  public_email: string | null;
  instagram_handle: string | null;
  facebook_url: string | null;
  tiktok_handle: string | null;
  cover_image_url: string | null;
  verified_at: string | null;
};

export type SalonLinkedStylist = {
  id: string;
  slug: string;
  display_name: string;
  profile_kind: string | null;
  profile_type: string | null;
  city: string | null;
  state_code: string | null;
  profile_image_url: string | null;
  headline: string | null;
};

/* ------------------------------------------------------------------ */

function serverPublic() {
  return createClient<Database>(
    OWNED_SUPABASE_URL,
    OWNED_SUPABASE_PUBLISHABLE_KEY,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

function showTestData(): boolean {
  try {
    const host = (getRequestHeader("host") ?? "").toLowerCase();
    if (!host) return false;
    if (host.startsWith("localhost") || host.startsWith("127.0.0.1")) return true;
    if (host.includes("id-preview--")) return true;
    if (host.endsWith("-dev.lovable.app")) return true;
    if (host.endsWith(".lovableproject.com")) return true;
    return false;
  } catch { return false; }
}

const SLUG_MAX = 200;




async function selectCitySummary(sb: ReturnType<typeof serverPublic>, cityId: string | null, stateId: string | null) {
  const [{ data: c }, { data: s }] = await Promise.all([
    cityId ? sb.from("cities").select("name").eq("id", cityId).maybeSingle() : Promise.resolve({ data: null as any }),
    stateId ? sb.from("states").select("state_code").eq("id", stateId).maybeSingle() : Promise.resolve({ data: null as any }),
  ]);
  const rawCity = (c?.name as string | null) ?? null;
  return {
    city: rawCity ? cleanCityLabel(rawCity) : null,
    state_code: (s?.state_code as string | null) ?? null,
  };
}


/* --------------------------- Public search ----------------------------
 * Canonical salon search. The source of truth is `nominees`
 * (record_type='salon'), NOT the `salons` extension table, so salon
 * profiles without an extension row are found too. Ranking happens in SQL
 * (public.search_salon_profiles) before the limit is applied.
 */

export type SalonSearchInput = {
  query: string;
  city?: string | null;
  stateCode?: string | null;
  website?: string | null;
  phone?: string | null;
  limit?: number;
};

export type SalonSearchResult = SalonSummary & {
  score: number;
  reasons: string[];
};

export const searchSalons = createServerFn({ method: "GET" })
  .validator((d: SalonSearchInput) =>
    z.object({
      query: z.string().max(120),
      city: z.string().max(120).nullable().optional(),
      stateCode: z.string().max(4).nullable().optional(),
      website: z.string().max(500).nullable().optional(),
      phone: z.string().max(40).nullable().optional(),
      limit: z.number().int().min(1).max(50).optional(),
    }).parse(d),
  )
  .handler(async ({ data }): Promise<SalonSearchResult[]> => {
    const q = (data.query ?? "").trim();
    if (q.length < 2) return [];
    const sb = serverPublic();
    const { data: rows, error } = await (sb as any).rpc("search_salon_profiles", {
      p_query: q,
      p_city: data.city?.trim() || null,
      p_state: data.stateCode?.trim() || null,
      p_website: data.website?.trim() || null,
      p_phone: data.phone?.trim() || null,
      p_limit: data.limit ?? 12,
    });
    if (error) {
      console.error("[searchSalons]", error.message);
      return [];
    }
    return ((rows ?? []) as any[]).map((r) => ({
      id: r.id as string,
      slug: r.slug as string,
      name: r.name as string,
      city: r.city ? cleanCityLabel(String(r.city)) : null,
      state_code: (r.state_code as string | null) ?? null,
      status: "verified" as SalonSummary["status"],
      logo_url: (r.logo_url as string | null) ?? null,
      claimed: !!r.claimed,
      score: Number(r.score ?? 0),
      reasons: (r.reasons as string[] | null) ?? [],
    }));
  });



/* --------------------------- Public detail ----------------------------
 * Canonical: the salon profile is a `nominees` row (record_type='salon').
 * The `salons` extension is optional and only supplies the operational
 * lifecycle status.
 */

export const getSalonBySlug = createServerFn({ method: "GET" })
  .validator((d: { slug: string }) => z.object({ slug: z.string().min(1).max(SLUG_MAX) }).parse(d))
  .handler(async ({ data }): Promise<
    | { redirectTo: string }
    | { salon: SalonDetail & { claimed: boolean }; stylists: SalonLinkedStylist[] }
    | null
  > => {
    const sb = serverPublic();
    const { data: nRow } = await sb
      .from("nominees_public")
      .select(
        "id, slug, display_name, record_type, profile_kind, status, bio, address, address_line_2, postal_code, city_id, state_id, raw_city, raw_state, website, instagram, facebook, tiktok, profile_image_url, cover_image_url, claim_status, claimed_by_user_id, is_test_data, sample_profile",
      )
      .eq("slug", data.slug)
      .maybeSingle();

    const na = nRow as any;
    const isSalonNominee =
      !!na && (na.record_type === "salon" || na.profile_kind === "salon");

    if (!isSalonNominee) {
      const { data: canonical } = await (sb as any).rpc("resolve_nominee_slug", { p_old_slug: data.slug });
      if (typeof canonical === "string" && canonical && canonical !== data.slug) {
        return { redirectTo: canonical };
      }
      return null;
    }

    // Lifecycle status is canonical nominee state: a hidden/suspended or
    // soft-deleted salon profile is not publicly visible.
    const status: SalonSummary["status"] = na.status === "active" ? "verified" : "inactive";
    if (status === "inactive") return null;

    const [loc, stylists, contactsRes] = await Promise.all([
      selectCitySummary(sb, na.city_id, na.state_id),
      loadSalonRoster(sb, na.id),
      sb.from("nominee_contacts_public")
        .select("contact_type,value,is_primary")
        .eq("nominee_id", na.id)
        .in("contact_type", ["email", "phone"])
        .order("is_primary", { ascending: false }),
    ]);
    if (contactsRes.error) throw new Error(contactsRes.error.message);
    const publicEmail = contactsRes.data?.find((contact: any) => contact.contact_type === "email")?.value ?? null;
    const publicPhone = contactsRes.data?.find((contact: any) => contact.contact_type === "phone")?.value ?? null;

    const salon: SalonDetail & { claimed: boolean } = {
      id: na.id, slug: na.slug, name: na.display_name,
      city: loc.city ?? (na.raw_city ? cleanCityLabel(na.raw_city) : null),
      state_code: loc.state_code ?? (na.raw_state ?? null),
      status,
      logo_url: na.profile_image_url ?? null,
      description: na.bio ?? null,
      address_line_1: na.address ?? null,
      address_line_2: na.address_line_2 ?? null,
      postal_code: na.postal_code ?? null,
      raw_city: na.raw_city ?? null,
      raw_state: na.raw_state ?? null,
      phone: publicPhone,
      website: na.website ?? null,
      public_email: publicEmail,
      instagram_handle: na.instagram ?? null,
      facebook_url: na.facebook ?? null,
      tiktok_handle: na.tiktok ?? null,
      cover_image_url: na.cover_image_url ?? null,
      verified_at: null,
      claimed: na.claim_status === "claimed",
    };
    return { salon, stylists };
  });

/** Publicly visible stylists whose current salon is this salon profile. */
async function loadSalonRoster(sb: any, salonId: string): Promise<SalonLinkedStylist[]> {
  const { data: linked } = await sb
    .from("nominees_public")
    .select("id, slug, display_name, profile_kind, headline, city_id, state_id, profile_image_url, is_test_data, sample_profile")
    .eq("current_salon_id", salonId)
    .eq("claim_status", "claimed")
    .not("slug", "is", null);
  const includeTest = showTestData();
  const visible = ((linked ?? []) as any[]).filter((n) => isProfilePubliclyVisible(n, includeTest));
  const cityIds = [...new Set(visible.map((n) => n.city_id).filter(Boolean))] as string[];
  const stateIds = [...new Set(visible.map((n) => n.state_id).filter(Boolean))] as string[];
  const [{ data: cities }, { data: states }] = await Promise.all([
    cityIds.length ? sb.from("cities").select("id, name").in("id", cityIds) : Promise.resolve({ data: [] as any[] }),
    stateIds.length ? sb.from("states").select("id, state_code").in("id", stateIds) : Promise.resolve({ data: [] as any[] }),
  ]);
  const cityMap = new Map<string, string>((cities ?? []).map((c: any) => [c.id as string, c.name as string]));
  const stateMap = new Map<string, string>((states ?? []).map((s: any) => [s.id as string, s.state_code as string]));
  return visible
    .map((n) => {
      const rawCity = cityMap.get(n.city_id) ?? null;
      return {
        id: n.id as string,
        slug: n.slug as string,
        display_name: n.display_name as string,
        profile_kind: (n.profile_kind as string | null) ?? null,
        profile_type: (n.profile_kind as string | null) ?? null,
        city: rawCity ? cleanCityLabel(rawCity) : null,
        state_code: stateMap.get(n.state_id) ?? null,
        profile_image_url: (n.profile_image_url as string | null) ?? null,
        headline: (n.headline as string | null) ?? null,
      };
    })
    .sort((a, b) => a.display_name.localeCompare(b.display_name));
}

/* ---------- Public salon context for the canonical /profile/$slug page ----------
 * Reads the nominee side only (salons rows are RLS-gated to verified rows),
 * so the roster and claim affordance work for any publicly visible salon
 * nominee — including unclaimed, stylist-suggested salons.
 */
export const getSalonProfileContext = createServerFn({ method: "GET" })
  .validator((d: { slug: string }) => z.object({ slug: z.string().min(1).max(SLUG_MAX) }).parse(d))
  .handler(async ({ data }): Promise<{ salonId: string; claimed: boolean; stylists: SalonLinkedStylist[] } | null> => {
    const sb = serverPublic();
    const { data: salonNominee } = await sb
      .from("nominees_public")
      .select("id, claim_status, record_type, profile_kind")
      .eq("slug", data.slug)
      .maybeSingle();
    const sn = salonNominee as any;
    if (!sn || (sn.record_type !== "salon" && sn.profile_kind !== "salon")) return null;

    const stylists = await loadSalonRoster(sb, sn.id as string);

    return { salonId: sn.id as string, claimed: sn.claim_status === "claimed", stylists };
  });



/* ------------------ Authenticated: current-salon actions ------------------ */

export const getMyCurrentSalon = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((input?: { nomineeId?: string | null }) => ({
    nomineeId: input?.nomineeId ? String(input.nomineeId) : null,
  }))
  .handler(async ({ context, data: args }): Promise<SalonSummary | null> => {
    const { supabase, userId } = context;
    const subject = await resolveEditableNominee(context as any, args?.nomineeId ?? null);
    if (isSubjectError(subject)) return null;
    const { data: n } = await supabase
      .from("nominees")
      .select("current_salon_id")
      .eq("id", subject.nomineeId)
      .maybeSingle();
    const salonId = (n as any)?.current_salon_id as string | null | undefined;
    if (!salonId) return null;
    // Canonical read: the linked salon is a nominee row. The `salons`
    // extension is optional and only supplies the verification status.
    const { data: s } = await supabase
      .from("nominees")
      .select("id, slug, display_name, profile_image_url, city_id, state_id, raw_city, raw_state, claimed_by_user_id, status, deleted_at")
      .eq("id", salonId)
      .maybeSingle();
    if (!s) return null;
    const sa = s as any;
    const [{ data: c }, { data: st }] = await Promise.all([
      sa.city_id ? supabase.from("cities").select("name").eq("id", sa.city_id).maybeSingle() : Promise.resolve({ data: null as any }),
      sa.state_id ? supabase.from("states").select("state_code").eq("id", sa.state_id).maybeSingle() : Promise.resolve({ data: null as any }),
    ]);
    const rawCity = (c?.name as string | null) ?? (sa.raw_city as string | null) ?? null;
    const claimed = !!sa.claimed_by_user_id;
    return {
      id: sa.id,
      slug: sa.slug,
      name: sa.display_name,
      status: (sa.status === "active" && !sa.deleted_at ? "verified" : "inactive") as SalonSummary["status"],
      logo_url: sa.profile_image_url ?? null,
      city: rawCity ? cleanCityLabel(rawCity) : null,
      state_code: (st?.state_code as string | null) ?? (sa.raw_state as string | null) ?? null,
      claimed,
      // Adding a salon never grants edit rights — only the approved owner edits.
      can_edit: claimed && sa.claimed_by_user_id === userId,
    };
  });



/**
 * Current affiliation state for the profile editor: the linked salon (if any)
 * plus the independent-professional flag. These are mutually exclusive.
 */
export const getMyAffiliation = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((input?: { nomineeId?: string | null }) => ({
    nomineeId: input?.nomineeId ? String(input.nomineeId) : null,
  }))
  .handler(async ({ context, data: args }): Promise<{ independent: boolean }> => {
    const { supabase } = context;
    const subject = await resolveEditableNominee(context as any, args?.nomineeId ?? null);
    if (isSubjectError(subject)) return { independent: false };
    const { data: n } = await supabase
      .from("nominees")
      .select("independent_professional")
      .eq("id", subject.nomineeId)
      .maybeSingle();
    return { independent: !!(n as any)?.independent_professional };
  });

/**
 * Canonical toggle for the independent state. Checking it clears the current
 * salon relationship (the salon profile itself is never deleted); unchecking
 * only clears the flag and never guesses a salon.
 */
export const setMyIndependentProfessional = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: { independent: boolean; nomineeId?: string | null }) =>
    z.object({ independent: z.boolean(), nomineeId: z.string().uuid().nullish() }).parse(d))
  .handler(async ({ context, data }) => {
    const { supabase } = context;
    const subject = await resolveEditableNominee(context as any, data.nomineeId ?? null);
    if (isSubjectError(subject)) throw new Error("No claimed profile found");
    const next = applyIndependentToggle(
      { currentSalonId: null, independent: !data.independent },
      data.independent,
    );
    const patch = next.independent
      ? // Independent always clears the relationship — never the salon record.
        { independent_professional: true, current_salon_id: null }
      : { independent_professional: false };
    const { error } = await supabase
      .from("nominees")
      .update(patch)

      .eq("id", subject.nomineeId);
    if (error) throw new Error("We couldn't update your affiliation. Please try again.");
    if (subject.claimedByUserId) await recalcMilestonesForUserSafe(subject.claimedByUserId);
    return { ok: true as const, independent: next.independent };
  });

export const linkMyCurrentSalon = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: { salonId: string; nomineeId?: string | null }) =>
    z.object({ salonId: z.string().uuid(), nomineeId: z.string().uuid().nullish() }).parse(d))
  .handler(async ({ context, data }) => {
    const { supabase } = context;
    const subject = await resolveEditableNominee(context as any, data.nomineeId ?? null);
    if (isSubjectError(subject)) throw new Error("No claimed profile found");
    const n = { id: subject.nomineeId };
    const linked = applySalonLink(data.salonId);
    const { error } = await supabase
      .from("nominees")
      .update({
        current_salon_id: linked.currentSalonId,
        // Linking a salon always leaves the independent state.
        independent_professional: linked.independent,
      })
      .eq("id", (n as any).id);

    if (error) {
      // Trigger blocks inactive salons and non-owned pending salons.
      throw new Error(
        error.message.includes("inactive")
          ? "This salon is not accepting new links."
          : error.message.includes("pending")
            ? "You can only link to a pending salon you suggested."
            : "We couldn't link this salon. Please try again.",
      );
    }
    // Truthful result: confirm the relationship actually persisted before
    // reporting success (an RLS-filtered update reports no error but no rows).
    const { data: after } = await supabase
      .from("nominees").select("current_salon_id").eq("id", (n as any).id).maybeSingle();
    if ((after as any)?.current_salon_id !== data.salonId) {
      throw new Error("We couldn't link this salon. Please try again.");
    }
    if (subject.claimedByUserId) await recalcMilestonesForUserSafe(subject.claimedByUserId);
    return { ok: true as const };
  });

export const unlinkMyCurrentSalon = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((input?: { nomineeId?: string | null }) => ({
    nomineeId: input?.nomineeId ? String(input.nomineeId) : null,
  }))
  .handler(async ({ context, data }) => {
    const { supabase } = context;
    const subject = await resolveEditableNominee(context as any, data?.nomineeId ?? null);
    if (isSubjectError(subject)) throw new Error("No claimed profile found");
    const n = { id: subject.nomineeId };
    const { error } = await supabase
      .from("nominees").update({ current_salon_id: null }).eq("id", (n as any).id);
    if (error) throw new Error("We couldn't remove the salon. Please try again.");
    if (subject.claimedByUserId) await recalcMilestonesForUserSafe(subject.claimedByUserId);
    return { ok: true as const };
  });

/* ------------------ Authenticated: add a missing salon ------------------
 * Canonical creation path. The profile is created directly in `nominees`
 * (record_type='salon', unclaimed) by public.create_missing_salon_profile.
 * No `salons` extension row is created, and the stylist gets provenance
 * (salon_profile_links, relationship='associated') — never ownership.
 *
 * The legacy salons-first `suggestSalon` path is retired below.
 */

export type SalonSuggestionInput = {
  name: string;
  city: string | null;
  stateCode: string | null;
  website?: string | null;
  addressLine1?: string | null;
  phone?: string | null;
  confirmCreate?: boolean;
  linkAfterCreate?: boolean;
};

export type SalonLinkError = "no_claimed_profile" | "self_link" | "link_not_persisted";

export type SalonSuggestionResult =
  | {
      ok: true;
      salon: SalonSummary;
      /** True only when the stylist's current_salon_id is verified on the server. */
      linked: boolean;
      linkRequested: boolean;
      linkError: SalonLinkError | null;
    }
  | { ok: false; code: "similar_exists"; matches: SalonSummary[] };

export const createMissingSalon = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: SalonSuggestionInput) =>
    z.object({
      name: z.string().min(2).max(200),
      city: z.string().min(1).max(120),
      stateCode: z.string().min(2).max(4),
      website: z.string().max(500).nullable().optional(),
      addressLine1: z.string().max(200).nullable().optional(),
      phone: z.string().max(40).nullable().optional(),
      confirmCreate: z.boolean().optional(),
      linkAfterCreate: z.boolean().optional(),
    }).parse(d),
  )
  .handler(async ({ context, data }): Promise<SalonSuggestionResult> => {
    const { supabase, userId } = context;
    const { data: res, error } = await (supabase as any).rpc("create_missing_salon_profile", {
      p_name: data.name.trim(),
      p_city: data.city.trim(),
      p_state: data.stateCode.trim(),
      p_website: data.website?.trim() || null,
      p_address: data.addressLine1?.trim() || null,
      p_phone: data.phone?.trim() || null,
      p_confirm: data.confirmCreate ?? false,
      p_link: data.linkAfterCreate !== false,
    });
    if (error) {
      const msg = String(error.message ?? "");
      if (msg.includes("SALON_LOCATION_REQUIRED")) {
        throw new Error("City and state are required to add a new salon.");
      }
      if (msg.includes("SALON_CITY_NOT_RECOGNIZED")) {
        throw new Error(
          "We couldn't match that city in the selected state. Pick a city from the suggestions and try again.",
        );
      }
      if (msg.includes("SALON_STATE_NOT_RECOGNIZED")) {
        throw new Error("That state isn't recognized. Choose a state from the list.");
      }
      if (msg.includes("SALON_NAME_REQUIRED")) {
        throw new Error("Salon name is too short.");
      }
      console.error("[createMissingSalon]", msg);
      throw new Error("We couldn't add this salon. Please try again.");
    }
    const payload = res as any;
    if (payload?.ok === false) {
      return {
        ok: false,
        code: "similar_exists",
        matches: ((payload.matches ?? []) as any[]).map((m) => ({
          id: m.id as string,
          slug: m.slug as string,
          name: m.name as string,
          status: "verified" as SalonSummary["status"],
          logo_url: (m.logo_url as string | null) ?? null,
          city: m.city ? cleanCityLabel(String(m.city)) : null,
          state_code: (m.state_code as string | null) ?? null,
          claimed: !!m.claimed,
        })),
      };
    }
    await recalcMilestonesForUserSafe(userId);
    const s = payload.salon ?? {};
    return {
      ok: true,
      linked: !!payload.linked,
      linkRequested: payload.link_requested !== false,
      linkError: (payload.link_error ?? null) as SalonLinkError | null,
      salon: {
        id: s.id as string,
        slug: s.slug as string,
        name: s.name as string,
        status: "verified",
        logo_url: null,
        city: s.city ? cleanCityLabel(String(s.city)) : null,
        state_code: (s.state_code as string | null) ?? null,
        suggested_by_me: true,
        can_edit: false,
      },
    };
  });

/**
 * RETIRED: the old `salons`-first suggestion path. Creating a salon profile
 * now goes through `createMissingSalon`, which writes the canonical
 * `nominees` record. Kept as a loud failure so stale bundles cannot write a
 * second profile record into `salons`.
 */
export const suggestSalon = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: { name?: string }) => z.object({ name: z.string().max(200).optional() }).parse(d))
  .handler(async () => {
    throw new Error("This path has moved. Please reload the page and add the salon again.");
  });


/* ------------------ Salon editing — confirmed owner only ------------------
 * Nominee-first: the editable record is the canonical `nominees` salon
 * profile. No `salons` extension row is required; the extension is consulted
 * only for the operational lifecycle status.
 */

export type SalonEditableFields = {
  name: string;
  description: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  postal_code: string | null;
  phone: string | null;
  website: string | null;
  public_email: string | null;
  instagram_handle: string | null;
  facebook_url: string | null;
  tiktok_handle: string | null;
  logo_url: string | null;
  cover_image_url: string | null;
};


export type SalonEditContext = {
  salon: SalonDetail & { claimed: boolean };
  mode: "owner" | "readonly";
  canEdit: boolean;
  reason?: string;
  hasPendingClaim: boolean;
};

/** Canonical salon profile row, resolved from `nominees` by slug. */
async function loadSalonDetailRow(sb: any, slug: string): Promise<any | null> {
  const { data: n } = await sb
    .from("nominees")
    .select(
      "id, slug, display_name, record_type, profile_kind, status, bio, address, address_line_2, postal_code, city_id, state_id, raw_city, raw_state, website, instagram, facebook, tiktok, profile_image_url, cover_image_url, claim_status, claimed_by_user_id, claimed_at, deleted_at, salon_verified_at",
    )
    .eq("slug", slug)
    .maybeSingle();
  const na = n as any;
  if (!na || na.deleted_at) return null;
  if (na.record_type !== "salon" && na.profile_kind !== "salon") return null;
  return {
    ...na,
    status: na.status === "active" ? "verified" : "inactive",
    verified_at: na.salon_verified_at ?? null,
  };
}

export const getSalonEditContext = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: { slug: string }) => z.object({ slug: z.string().min(1).max(SLUG_MAX) }).parse(d))
  .handler(async ({ context, data }): Promise<SalonEditContext | null> => {
    const { supabase, userId } = context;
    const s = await loadSalonDetailRow(supabase, data.slug);
    if (!s) return null;

    const loc = await selectCitySummary(supabase, s.city_id, s.state_id);
    const claimed = !!s.claimed_by_user_id;
    const isOwner = s.claimed_by_user_id === userId;

    // Adding/suggesting a salon never grants editing rights. Only the
    // approved owner (via the claim flow) or an admin can edit a salon.
    let mode: SalonEditContext["mode"] = "readonly";
    let canEdit = false;
    let reason: string | undefined;
    if (s.status === "inactive") {
      reason = "This salon is inactive and cannot be edited.";
    } else if (isOwner) {
      mode = "owner"; canEdit = true;
    } else if (claimed) {
      reason = "This salon has been claimed. Only the approved claimant can edit it.";
    } else {
      reason = "This salon profile is unclaimed. Claim it to manage its details.";
    }

    // Pending ownership request submitted by this user (canonical path).
    const { data: pending } = await supabase
      .from("salon_ownership_requests")
      .select("id")
      .eq("salon_id", s.id)
      .eq("requester_user_id", userId)
      .in("status", ["submitted", "under_review"])
      .maybeSingle();

    const { data: primaryContacts, error: contactError } = await supabase
      .from("nominee_contacts")
      .select("contact_type,value")
      .eq("nominee_id", s.id)
      .in("contact_type", ["email", "phone"])
      .eq("is_primary", true);
    if (contactError) throw new Error(contactError.message);

    const salon: SalonDetail & { claimed: boolean } = {
      id: s.id, slug: s.slug, name: s.display_name,
      status: (s.status as SalonSummary["status"]) ?? "verified",
      city: loc.city ?? (s.raw_city ? cleanCityLabel(s.raw_city) : null),
      state_code: loc.state_code ?? (s.raw_state ?? null),
      logo_url: s.profile_image_url ?? null,
      description: s.bio ?? null,
      address_line_1: s.address ?? null,
      address_line_2: s.address_line_2 ?? null,
      postal_code: s.postal_code ?? null,
      raw_city: s.raw_city ?? null,
      raw_state: s.raw_state ?? null,
      phone: primaryContacts?.find((contact: any) => contact.contact_type === "phone")?.value ?? null,
      website: s.website ?? null,
      public_email: primaryContacts?.find((contact: any) => contact.contact_type === "email")?.value ?? null,
      instagram_handle: s.instagram ?? null,
      facebook_url: s.facebook ?? null,
      tiktok_handle: s.tiktok ?? null,
      cover_image_url: s.cover_image_url ?? null,
      verified_at: s.verified_at ?? null,
      claimed,
    };
    return { salon, mode, canEdit, reason, hasPendingClaim: !!pending };
  });

const POSTAL_RE = /^[0-9]{5}(-[0-9]{4})?$/;

const editableSchema = z.object({
  slug: z.string().min(1).max(SLUG_MAX),
  fields: z.object({
    name: z.string().min(2).max(200),
    description: z.string().max(5000).nullable(),
    address_line_1: z.string().max(200).nullable(),
    address_line_2: z.string().max(200).nullable(),
    postal_code: z.string().max(20).nullable().refine(
      (v) => v == null || v.trim() === "" || POSTAL_RE.test(v.trim()),
      { message: "Enter a valid US ZIP (e.g. 12345 or 12345-6789)." },
    ),
    phone: z.string().max(40).nullable(),
    website: z.string().max(500).nullable(),
    public_email: z.string().max(200).nullable(),
    instagram_handle: z.string().max(60).nullable(),
    facebook_url: z.string().max(500).nullable(),
    tiktok_handle: z.string().max(60).nullable(),
    logo_url: z.string().max(1000).nullable(),
    cover_image_url: z.string().max(1000).nullable(),
  }),
});

/** Resolve a salon slug to the canonical nominee id (owner-visible). */
async function resolveSalonId(supabase: any, slug: string): Promise<string> {
  const { data: n } = await supabase
    .from("nominees")
    .select("id, record_type, profile_kind")
    .eq("slug", slug)
    .maybeSingle();
  const na = n as any;
  if (!na || (na.record_type !== "salon" && na.profile_kind !== "salon")) {
    throw new Error("Salon not found.");
  }
  return na.id as string;
}

export const updateSalon = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: { slug: string; fields: SalonEditableFields }) => editableSchema.parse(d))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    // Canonical write: public salon profile fields live on `nominees`.
    // Location (city/state) is handled by updateSalonLocation.
    const salonId = await resolveSalonId(supabase, data.slug);
    const { error } = await (supabase as any).rpc("update_salon_profile", {
      p_salon_id: salonId,
      p_name: data.fields.name.trim(),
      p_description: data.fields.description,
      p_address_line_1: data.fields.address_line_1,
      p_address_line_2: data.fields.address_line_2,
      p_postal_code: data.fields.postal_code,
      p_phone: data.fields.phone,
      p_website: data.fields.website,
      p_public_email: data.fields.public_email,
      p_instagram: data.fields.instagram_handle,
      p_facebook: data.fields.facebook_url,
      p_tiktok: data.fields.tiktok_handle,
      p_logo_url: data.fields.logo_url,
      p_cover_image_url: data.fields.cover_image_url,
    });
    if (error) {
      console.error("[updateSalon]", error.message);
      throw new Error("You don't have permission to edit this salon, or the change was blocked.");
    }
    await recalcMilestonesForUserSafe(userId);
    return { ok: true as const };
  });

export const updateSalonLocation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: { slug: string; state_id: string | null; city_id: string | null }) =>
    z.object({
      slug: z.string().min(1).max(SLUG_MAX),
      state_id: z.string().uuid().nullable(),
      city_id: z.string().uuid().nullable(),
    }).parse(d),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const salonId = await resolveSalonId(supabase, data.slug);
    const { error } = await (supabase as any).rpc("update_salon_location", {
      p_salon_id: salonId,
      p_state_id: data.state_id,
      p_city_id: data.city_id,
    });


    if (error) {
      const msg = String(error.message ?? "");
      if (msg.includes("SALON_LOCATION_INVALID")) {
        throw new Error(
          "SALON_LOCATION_INVALID: Please select a state and a city that belongs to that state.",
        );
      }
      if ((error as any).code === "42501" || msg.toLowerCase().includes("permission")) {
        throw new Error("You don't have permission to edit this salon's location.");
      }
      console.error("[updateSalonLocation]", msg);
      throw new Error("We couldn't save the salon location. Please try again.");
    }
    await recalcMilestonesForUserSafe(userId);
    return { ok: true as const };
  });


/**
 * RETIRED. The legacy `salon_claims` inline claim path is no longer used.
 * The single canonical claim path is `/claim-profile/verify/$nomineeId`
 * (verification code, or an ownership request when no contact is on file).
 * Kept only so any stale client bundle fails loudly instead of writing to a
 * table that no longer drives ownership.
 */
export const submitSalonClaim = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: { salonId: string }) => z.object({ salonId: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    throw new Error(
      `This claim path has moved. Please claim this salon at /claim-profile/verify/${data.salonId}.`,
    );
  });
